// Helpers para manejar el tipo de alquiler (regular vs admin) en la UI unificada.
import { useSearchParams } from 'react-router-dom';

export const RENTAL_TYPE = {
  REGULAR: 'regular',
  ADMIN: 'admin',
};

/**
 * Hook para leer el tipo de alquiler actual desde el query param.
 * Devuelve 'admin' si ?tipo=admin, sino 'regular'.
 */
export const useRentalType = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get('tipo') === RENTAL_TYPE.ADMIN
    ? RENTAL_TYPE.ADMIN
    : RENTAL_TYPE.REGULAR;
};

/**
 * Devuelve el path al detalle de un contrato según su tipo.
 */
export const getDetailPath = (contractId, type, sub = '') => {
  const base = `/alquileres/contrato/${contractId}${sub ? `/${sub}` : ''}`;
  return type === RENTAL_TYPE.ADMIN ? `${base}?tipo=admin` : base;
};

/**
 * Normaliza un contrato (regular o admin) a una forma común para listas/dashboards.
 * No muta el original.
 */
export const normalizeRental = (item, type) => {
  if (!item) return null;
  if (type === RENTAL_TYPE.ADMIN) {
    return {
      _raw: item,
      _type: RENTAL_TYPE.ADMIN,
      id: item.id,
      propertyAddress: item.propertyAddress || '',
      propertyType: item.propertyType || '',
      propertyDepartment: item.propertyDepartment || '',
      propertyReference: item.propertyReference || '',
      ownerName: item.ownerName || '',
      ownerPhone: item.ownerPhone || '',
      ownerEmail: item.ownerEmail || '',
      currency: item.currency || 'ARS',
      currentRentValue: item.currentRentValue ?? item.rentValue ?? 0,
      contractStart: item.contractStart,
      contractEnd: item.contractEnd,
      contractStatus: item.contractStatus || (item.isActive ? 'active' : 'pending'),
      isActive: !!item.isActive,
      paymentDueDay: item.paymentDueDay,
      tenants: item.tenants || [],
    };
  }
  // regular
  const property = item.property || {};
  const owner = property.owner || item.owner || {};
  return {
    _raw: item,
    _type: RENTAL_TYPE.REGULAR,
    id: item.id,
    propertyAddress:
      property.propertyAddress ||
      property.direccion ||
      item.propertyAddress ||
      '',
    propertyType: property.propertyType || property.tipoDePropiedad || '',
    propertyDepartment: property.department || property.departamento || '',
    propertyReference: property.referencia || '',
    ownerName: owner.name || owner.fullName || item.ownerName || '',
    ownerPhone: owner.phone || owner.telefono || '',
    ownerEmail: owner.email || '',
    currency: item.currency || 'ARS',
    currentRentValue:
      item.currentRentValue ?? item.monthlyRent ?? item.rentValue ?? 0,
    contractStart: item.contractStart || item.startDate,
    contractEnd: item.contractEnd || item.endDate,
    contractStatus:
      item.contractStatus || item.status || (item.isActive ? 'active' : 'pending'),
    isActive: !!item.isActive,
    paymentDueDay: item.paymentDueDay,
    tenants: item.tenants || [],
  };
};

/**
 * Normaliza una cuota (payment) a forma común.
 */
export const normalizePayment = (payment, type) => {
  if (!payment) return null;
  if (type === RENTAL_TYPE.ADMIN) {
    const r = payment.adminRental || {};
    return {
      _raw: payment,
      _type: RENTAL_TYPE.ADMIN,
      id: payment.id,
      contractId: payment.adminRentalId || r.id,
      paymentNumber: payment.paymentNumber,
      dueDate: payment.dueDate,
      totalAmount: payment.totalAmount,
      amountPaid: payment.amountPaid,
      currency: payment.currency || r.currency || 'ARS',
      status: payment.status || 'pending',
      daysOverdue:
        payment.daysOverdueCalculated ?? payment.daysOverdue ?? 0,
      lateInterest:
        payment.estimatedLateInterest ?? payment.lateInterest ?? 0,
      propertyAddress: r.propertyAddress || '',
      ownerName: r.ownerName || '',
    };
  }
  // regular
  const r = payment.rental || {};
  const property = r.property || {};
  const owner = property.owner || {};
  return {
    _raw: payment,
    _type: RENTAL_TYPE.REGULAR,
    id: payment.id,
    contractId: payment.rentalId || r.id,
    paymentNumber: payment.paymentNumber,
    dueDate: payment.dueDate,
    totalAmount: payment.totalAmount ?? payment.amount,
    amountPaid: payment.amountPaid,
    currency: payment.currency || r.currency || 'ARS',
    status: payment.paymentStatus || payment.status || 'pending',
    daysOverdue:
      payment.daysOverdueCalculated ?? payment.daysOverdue ?? 0,
    lateInterest:
      payment.estimatedLateInterest ?? payment.lateInterest ?? 0,
    propertyAddress: property.propertyAddress || property.direccion || '',
    ownerName: owner.name || owner.fullName || '',
  };
};

export const formatCurrency = (amount, currency = 'ARS') =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'ARS',
    minimumFractionDigits: 0,
  }).format(Number(amount) || 0);

export const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('es-AR') : '-';

export const typeLabel = (type) =>
  type === RENTAL_TYPE.ADMIN ? 'Solo Admin' : 'Regular';
